import { useCallback, useEffect, useId, useState } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import type { AuthSessionResponse, SessionListItem } from '@brewcore/app-contract';
import {
  agoText,
  reasonText,
  t,
  type AuthClient,
  type Lang,
  type NetState,
  type TextKey,
} from '../core/index.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  FitLine,
  Icon,
  Sheet,
  StatusDot,
  type IconName,
  type Tone,
} from './components/index.js';

const netTone: Record<NetState, Tone> = {
  connecting: 'warn',
  ok: 'ok',
  cloud_unreachable: 'danger',
  device_offline: 'danger',
};

/**
 * Account (FR-2) as a bottom sheet: who is signed in, the cloud connection, pairing another machine, the device
 * list with "end", linking Google and, for the operator, setting up a passkey (both need a fresh login, FR-48),
 * language, sign out here or everywhere.
 */
export function AccountSheet({
  auth,
  session,
  lang,
  net,
  onClose,
  onSignedOut,
  onToggleLang,
  onPair,
}: {
  auth: AuthClient;
  session: AuthSessionResponse;
  lang: Lang;
  net: NetState;
  onClose: () => void;
  onSignedOut: () => void;
  onToggleLang: () => void;
  /** "pair another machine" (only offered once a machine exists; the first one comes from the empty state) */
  onPair?: () => void;
}) {
  const [devices, setDevices] = useState<SessionListItem[]>([]);
  // a refusal (warn) or the one confirmation that matters here: the passkey is set up (info)
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [pickingLang, setPickingLang] = useState(false);
  const langTitleId = useId();

  const load = useCallback(async () => {
    const r = await auth.sessions();
    if (r.ok) setDevices(r.value);
  }, [auth]);

  useEffect(() => {
    void load();
  }, [load]);

  const say = (code: string) =>
    setMessage({
      text:
        code === 'fresh_login' ? t(lang, 'freshNeeded') : reasonText(lang, code) || t(lang, 'loginFailed'),
      ok: false,
    });

  const signOut = async (everywhere: boolean) => {
    setBusy(true);
    await (everywhere ? auth.logoutEverywhere() : auth.logout());
    setBusy(false);
    onSignedOut();
  };

  const end = async (id: string, current: boolean) => {
    const r = await auth.endSession(id);
    if (!r.ok) return say(r.error);
    if (current) return onSignedOut();
    await load();
  };

  const linkGoogle = async () => {
    const r = await auth.startGoogle('/', true);
    if (!r.ok) return say(r.error);
    location.assign(r.value.url);
  };

  const addPasskey = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const options = await auth.passkeyRegisterOptions();
      if (!options.ok) return say(options.error);
      const response = await startRegistration({ optionsJSON: options.value as never });
      const done = await auth.passkeyRegister(response, `Brewcore ${new Date().toLocaleDateString(lang)}`);
      if (!done.ok) return say(done.error);
      setMessage({ text: t(lang, 'passkeyAdded'), ok: true });
    } catch {
      setMessage({ text: t(lang, 'loginFailed'), ok: false });
    } finally {
      setBusy(false);
    }
  };

  const now = Date.now();
  const operator = session.user.role === 'operator';
  return (
    <Sheet
      title={t(lang, 'account')}
      titleId="account-title"
      onClose={onClose}
      closeLabel={t(lang, 'close')}
      closeTestId="account-close"
      testId="account"
    >
      {/* who is signed in, the role and the cloud connection under the address (prototype "Konto") */}
      <div className="account-id">
        <span className="avatar avatar-lg" aria-hidden="true">
          {(session.user.name.trim() || session.user.email).charAt(0).toUpperCase()}
        </span>
        <div className="account-id-text">
          <span className="account-mail">{session.user.email}</span>
          <span className="hint account-sub">
            {operator ? t(lang, 'operator') : t(lang, 'user')} ·
            <span className="conn" data-testid="account-cloud">
              <StatusDot tone={netTone[net]} />
              {t(lang, `net_${net}`)}
            </span>
          </span>
        </div>
      </div>

      {message && (
        <Banner
          tone={message.ok ? 'info' : 'warn'}
          icon={message.ok ? 'check' : 'alert'}
          role="status"
          data-testid="account-message"
        >
          {message.text}
        </Banner>
      )}

      {/* the account's actions as rows with an icon, like the settings (the app's stacked buttons before) */}
      <Card className="nav-card account-actions">
        <ul className="list nav-list">
          {onPair && (
            <AccountRow icon="plus" label={t(lang, 'pairAnother')} onClick={onPair} testId="pair-open" />
          )}
          <AccountRow
            icon="link"
            label={t(lang, 'linkGoogle')}
            onClick={() => void linkGoogle()}
            disabled={busy}
          />
          {operator && (
            <AccountRow
              icon="key"
              label={t(lang, 'passkeyAdd')}
              onClick={() => void addPasskey()}
              disabled={busy}
              testId="passkey-add"
            />
          )}
          {/* the row opens the choice; a tap on it changed the language at once before (Dominik 22.10.2026) */}
          <AccountRow
            icon="globe"
            label={t(lang, 'languageRow')}
            value={t(lang, 'languageNow')}
            onClick={() => setPickingLang(true)}
            testId="lang"
          />
        </ul>
      </Card>

      <Card className="devices-card">
        {/* the title alone, as on the board "Konto" (23.10.2026: "Angemeldete Geräte" wrapped next to the link) */}
        <CardHeader title={t(lang, 'devices')} />
        <ul className="devices" data-testid="devices">
          {devices.map((d) => (
            <li key={d.id}>
              {/* prototype "Konto": the device and how it signed in, below it "dieses Gerät · zuletzt aktiv …"; two
                  lines that never wrap, the later parts drop out where they do not fit (WRITING.md 0a) */}
              <FitLine
                className="device-name"
                parts={[d.device, t(lang, `method_${d.authMethod}` as TextKey)]}
              />
              <FitLine
                className="hint"
                parts={[
                  d.current ? t(lang, 'thisDevice') : null,
                  `${t(lang, 'lastActive')} ${agoText(lang, d.lastActiveAt, now)}`,
                ]}
              />
              <button type="button" className="link-btn" onClick={() => void end(d.id, d.current)}>
                {t(lang, 'endSession')}
              </button>
            </li>
          ))}
        </ul>
        {/* signing out everywhere under the list, only where there is another device to end */}
        {devices.length > 1 && (
          <button
            type="button"
            className="link-btn devices-all"
            onClick={() => void signOut(true)}
            disabled={busy}
            data-testid="logout-all"
          >
            {t(lang, 'logoutAll')}
          </button>
        )}
      </Card>

      <Button
        variant="secondary"
        block
        icon="logout"
        onClick={() => void signOut(false)}
        disabled={busy}
        data-testid="logout"
      >
        {t(lang, 'logout')}
      </Button>
      {pickingLang && (
        <Sheet
          variant="sheet"
          title={t(lang, 'languageRow')}
          titleId={langTitleId}
          onClose={() => setPickingLang(false)}
          testId="lang-sheet"
        >
          <Card className="nav-card">
            <ul className="list nav-list" role="radiogroup" aria-labelledby={langTitleId}>
              {LANGS.map((l) => (
                <li className="nav-item" key={l.id}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={l.id === lang}
                    className="nav-row"
                    lang={l.id}
                    onClick={() => {
                      if (l.id !== lang) onToggleLang();
                      setPickingLang(false);
                    }}
                    data-testid={`lang-${l.id}`}
                  >
                    <span className="row-text">
                      <span className="row-label">{l.name}</span>
                    </span>
                    {l.id === lang && <Icon name="check" size="sm" className="list-check" />}
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        </Sheet>
      )}
    </Sheet>
  );
}

/** A row of the account sheet: icon, label, its value at the right, the chevron; the whole row is the button. */
/** the app's languages, each named in its own language */
const LANGS: { id: Lang; name: string }[] = [
  { id: 'de', name: 'Deutsch' },
  { id: 'en', name: 'English' },
];

function AccountRow({
  icon,
  label,
  value,
  onClick,
  disabled,
  testId,
}: {
  icon: IconName;
  label: string;
  value?: string;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <li className="nav-item">
      <button type="button" className="nav-row" onClick={onClick} disabled={disabled} data-testid={testId}>
        <Icon name={icon} />
        <span className="row-text">
          <span className="row-label">{label}</span>
        </span>
        {value && <span className="row-aside">{value}</span>}
        <Icon name="chevronRight" size="sm" className="list-chevron" />
      </button>
    </li>
  );
}
